import { Injectable, signal, computed } from '@angular/core';
import { ConfigurationProxy, KeyValueModel } from '@/app/proxy/proxies';
import { firstValueFrom, catchError, of } from 'rxjs';
import { SessionStorageService } from '../storage/session-storage.service';

interface AuthenticatedConfig {
  mapboxApiToken: string;
  mapboxStyle: string;
}

export interface LanguageResource {
  key: string;
  iso1: string;
  url: string;
}

interface AppConfiguration {
  localization: LanguageResource[];
}

interface RetryConfig {
  maxRetries: number;
  baseDelayMs: number;
  maxDelayMs: number;
}

enum InitializationState {
  IDLE = 'idle',
  LOADING = 'loading',
  SUCCESS = 'success',
  FAILED = 'failed',
}

class AppConfigKeys {
  languageKey = 'App.Fieldwork.Language.File.URL';
}

@Injectable({
  providedIn: 'root',
})
export class AppConfigService {
  private readonly MAPBOX_TOKEN_KEY = 'Core.Mapbox.Api.Token.Public';
  private readonly MAPBOX_STYLE_KEY = 'Core.Mapbox.Style';
  private readonly SESSION_KEY = 'fieldwork_authenticated_config';

  private readonly _config = signal<AuthenticatedConfig | null>(null);
  private readonly _isLoading = signal<boolean>(false);
  private readonly _isInitialized = signal<boolean>(false);

  // Computed signals for easy access
  readonly mapboxApiToken = computed(() => this._config()?.mapboxApiToken ?? '');
  readonly mapboxStyle = computed(
    () => this._config()?.mapboxStyle ?? 'mapbox://styles/mapbox/satellite-streets-v12',
  );
  readonly isLoading = this._isLoading.asReadonly();
  readonly isInitialized = this._isInitialized.asReadonly();

  // App-level configuration (localization and others)
  private readonly APP_SESSION_RAW_KEY = 'fieldwork_app_configurations';
  private readonly APP_SESSION_OBJECT_KEY = 'fieldwork_app_configuration_object';

  private readonly _retryConfig: RetryConfig = {
    maxRetries: 3,
    baseDelayMs: 1000,
    maxDelayMs: 10000,
  };

  private readonly _appConfigKeys = new AppConfigKeys();
  private _appConfigurations: Record<string, string> | undefined;
  private _appConfigurationObject: AppConfiguration | undefined;
  private _initState: InitializationState = InitializationState.IDLE;
  private _initPromise: Promise<boolean> | null = null;

  constructor(
    private readonly configurationProxy: ConfigurationProxy,
    private readonly sessionStorageService: SessionStorageService,
  ) {
    // Try to load authenticated config from session storage on init
    this.loadFromSessionStorage();
  }

  // =================== AUTHENTICATED CONFIGURATION (MAPBOX) ===================

  private loadFromSessionStorage(): void {
    const cached = this.sessionStorageService.getItem<AuthenticatedConfig>(this.SESSION_KEY);
    if (cached) {
      this._config.set(cached);
      this._isInitialized.set(true);
    }
  }

  private saveToSessionStorage(config: AuthenticatedConfig): void {
    this.sessionStorageService.setItem(this.SESSION_KEY, config);
  }

  async initializeAuthenticatedConfigurations(): Promise<boolean> {
    if (this._isInitialized()) {
      return true;
    }

    if (this._isLoading()) {
      // Wait for existing initialization
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!this._isLoading()) {
            clearInterval(checkInterval);
            resolve(this._isInitialized());
          }
        }, 100);
      });
    }

    this._isLoading.set(true);

    try {
      const response = await firstValueFrom(
        this.configurationProxy
          .configuration_GetConfiguration([this.MAPBOX_TOKEN_KEY, this.MAPBOX_STYLE_KEY])
          .pipe(
            catchError((error) => {
              console.warn('Failed to fetch authenticated configurations:', error);
              return of(null);
            }),
          ),
      );

      if (response?.result) {
        const resultData = response.result as unknown as KeyValueModel[];

        const config: AuthenticatedConfig = {
          mapboxApiToken: '',
          mapboxStyle: 'mapbox://styles/mapbox/satellite-streets-v12',
        };

        if (Array.isArray(resultData)) {
          for (const item of resultData) {
            if (item.key === this.MAPBOX_TOKEN_KEY) {
              config.mapboxApiToken = item.value ?? '';
            } else if (item.key === this.MAPBOX_STYLE_KEY) {
              config.mapboxStyle = item.value ?? config.mapboxStyle;
            }
          }
        }

        this._config.set(config);
        this.saveToSessionStorage(config);
        this._isInitialized.set(true);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to load authenticated configurations:', error);
      return false;
    } finally {
      this._isLoading.set(false);
    }
  }

  clearAuthenticatedConfigurations(): void {
    this._config.set(null);
    this._isInitialized.set(false);
    this.sessionStorageService.removeItem(this.SESSION_KEY);
  }

  async forceRefreshAuthenticatedConfigurations(): Promise<boolean> {
    this._isInitialized.set(false);
    this.sessionStorageService.removeItem(this.SESSION_KEY);
    return this.initializeAuthenticatedConfigurations();
  }

  // =================== APP CONFIGURATION (LOCALIZATION) ===================

  async initializeApp(): Promise<boolean> {
    if (this._initState === InitializationState.SUCCESS) {
      return true;
    }

    if (this._initState === InitializationState.FAILED) {
      return false;
    }

    if (this._initState === InitializationState.LOADING && this._initPromise) {
      return this._initPromise;
    }

    const cachedRaw = this.sessionStorageService.getItem<Record<string, string>>(
      this.APP_SESSION_RAW_KEY,
    );
    const cachedObject = this.sessionStorageService.getItem<AppConfiguration>(
      this.APP_SESSION_OBJECT_KEY,
    );
    if (cachedRaw && cachedObject) {
      this._appConfigurations = cachedRaw;
      this._appConfigurationObject = cachedObject;
      this._initState = InitializationState.SUCCESS;
      return true;
    }

    this._initState = InitializationState.LOADING;
    this._initPromise = this.performAppConfigurationsInitialization();
    return this._initPromise;
  }

  private async performAppConfigurationsInitialization(): Promise<boolean> {
    const keys = [this._appConfigKeys.languageKey];

    for (let attempt = 0; attempt <= this._retryConfig.maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          const delayMs = Math.min(
            this._retryConfig.baseDelayMs * Math.pow(2, attempt - 1),
            this._retryConfig.maxDelayMs,
          );
          await this.delay(delayMs);
        }

        const response = await firstValueFrom(
          this.configurationProxy.configuration_GetConfiguration(keys).pipe(
            catchError((error) => {
              console.error(`AppConfig: HTTP error on attempt ${attempt + 1}:`, error);
              throw error;
            }),
          ),
        );

        if (response?.result && this.parseAndConfigureResponse(response.result)) {
          this._initState = InitializationState.SUCCESS;
          this._initPromise = null;
          return true;
        }

        console.warn(`AppConfig: Empty or invalid response on attempt ${attempt + 1}`);
      } catch (error) {
        console.error(
          `AppConfig: Failed attempt ${attempt + 1}/${this._retryConfig.maxRetries + 1}:`,
          error,
        );

        if (attempt === this._retryConfig.maxRetries) {
          console.error('AppConfig: All retry attempts exhausted. Initialization failed.');
          this._initState = InitializationState.FAILED;
          this._initPromise = null;
          this.clearAppSessionStorage();
          return false;
        }
      }
    }

    this._initState = InitializationState.FAILED;
    this._initPromise = null;
    this.clearAppSessionStorage();
    return false;
  }

  private parseAndConfigureResponse(result: unknown): boolean {
    try {
      let parsed: unknown = result;

      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }

      if (!Array.isArray(parsed)) {
        console.error('AppConfig: Expected array but got', typeof parsed);
        return false;
      }

      const data = parsed as KeyValueModel[];
      this.configureAppConfigurations(data);
      return true;
    } catch (error) {
      console.error('AppConfig: Error parsing configuration JSON:', error);
      return false;
    }
  }

  private configureAppConfigurations(data: KeyValueModel[]): void {
    const configMap = data.reduce(
      (acc, item) => {
        acc[item.key] = item.value ?? '';
        return acc;
      },
      {} as Record<string, string>,
    );

    this._appConfigurations = configMap;
    this._appConfigurationObject = this.mapToConfigurationObject(configMap);
    this.saveAppToSessionStorage();
  }

  private mapToConfigurationObject(configMap: Record<string, string>): AppConfiguration {
    return {
      localization: this.parseLocalization(configMap[this._appConfigKeys.languageKey] ?? ''),
    };
  }

  private parseLocalization(localizationJson: string): LanguageResource[] {
    if (!localizationJson) {
      return [];
    }

    try {
      const parsed = JSON.parse(localizationJson);

      if (!Array.isArray(parsed)) {
        console.error('Invalid localization config format: expected array');
        return [];
      }

      return parsed.filter(
        (item: any) =>
          item &&
          typeof item.key === 'string' &&
          typeof item.iso1 === 'string' &&
          typeof item.url === 'string',
      );
    } catch (error) {
      console.error('Error parsing localization JSON:', error);
      return [];
    }
  }

  private saveAppToSessionStorage(): void {
    if (this._appConfigurations) {
      this.sessionStorageService.setItem(this.APP_SESSION_RAW_KEY, this._appConfigurations);
    }
    if (this._appConfigurationObject) {
      this.sessionStorageService.setItem(
        this.APP_SESSION_OBJECT_KEY,
        this._appConfigurationObject,
      );
    }
  }

  private clearAppSessionStorage(): void {
    this.sessionStorageService.removeItem(this.APP_SESSION_RAW_KEY);
    this.sessionStorageService.removeItem(this.APP_SESSION_OBJECT_KEY);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Localization helpers

  get localizationConfig(): LanguageResource[] {
    return this._appConfigurationObject?.localization ?? [];
  }

  localizationUrl(language: string): string {
    return this.localizationConfig.find((lr) => lr.iso1 === language)?.url ?? '';
  }

  localizationUrlByKey(key: string): string {
    return this.localizationConfig.find((lr) => lr.key === key)?.url ?? '';
  }

  keyToIso1(key: string): string | null {
    return this.localizationConfig.find((lr) => lr.key === key)?.iso1 ?? null;
  }

  iso1ToKey(iso1: string): string | null {
    return this.localizationConfig.find((lr) => lr.iso1 === iso1)?.key ?? null;
  }
}
